import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { jobStatusClass } from './job-status-style';

@Component({
  selector: 'app-admin-dispatch-ready',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <!-- Template remains mostly identical using signals and direct bindings -->
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      @if (!selectedJob()) {
        <div class="max-w-[1700px] mx-auto p-5">
        <div class="flex items-center justify-between mb-5 shrink-0">
          <div>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">Quote Follow-up &amp; Office Review</h1>
            <p class="text-slate-500 text-xs">Quote-sent jobs, field-complete office reviews, and jobs closed after that stage.</p>
          </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2.5 mb-4">
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Office Queue</p><p class="text-lg font-semibold text-slate-900 mt-0.5">{{ jobs().length }}</p></div>
            <span class="w-8 h-8 rounded-lg bg-sky-50 border border-sky-100 text-sky-600 flex items-center justify-center"><i class="ti ti-clipboard-data"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-cyan-200 shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div><p class="text-[10px] uppercase tracking-widest font-bold text-cyan-600">Quote Follow-up</p><p class="text-lg font-semibold text-cyan-700 mt-0.5">{{ statusCount('Quote Sent') }}</p></div>
            <span class="w-8 h-8 rounded-lg bg-cyan-50 border border-cyan-100 text-cyan-600 flex items-center justify-center"><i class="ti ti-file-dollar"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Needs Review</p><p class="text-lg font-semibold text-[#1A6FA6] mt-0.5">{{ statusCount('Ready for Office') }}</p></div>
            <span class="w-8 h-8 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] flex items-center justify-center"><i class="ti ti-file-invoice"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Closed</p><p class="text-lg font-semibold text-emerald-700 mt-0.5">{{ closedCount() }}</p></div>
            <span class="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center"><i class="ti ti-circle-check"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-rose-200 shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div><p class="text-[10px] uppercase tracking-widest font-bold text-rose-400">Safety Declined</p><p class="text-lg font-semibold text-rose-700 mt-0.5">{{ safetyDeclinedCount() }}</p></div>
            <span class="w-8 h-8 rounded-lg bg-rose-50 border border-rose-100 text-rose-600 flex items-center justify-center"><i class="ti ti-shield-x"></i></span>
          </div>
        </div>

        <div class="jobs-grid">
          @if (isLoading()) {
            @for (n of skeletons; track n) {
              <div class="bg-white rounded-3xl shadow-sm p-6 border border-slate-100 animate-pulse">
                <div class="flex items-center gap-2 mb-4">
                  <div class="h-5 w-20 bg-slate-200 rounded-full"></div>
                  <div class="h-4 w-10 bg-slate-100 rounded-full"></div>
                </div>
                <div class="h-5 w-3/4 bg-slate-200 rounded-xl mb-2"></div>
                <div class="h-4 w-1/2 bg-slate-100 rounded-xl mb-4"></div>
                <div class="bg-slate-100 rounded-2xl p-4 h-16 mb-4"></div>
                <div class="h-4 w-1/3 bg-slate-100 rounded-xl mb-2"></div>
                <div class="flex justify-end pt-4 border-t border-slate-100">
                  <div class="h-8 w-28 bg-slate-200 rounded-xl"></div>
                </div>
              </div>
            }
          } @else if (jobs().length === 0) {
            <div class="col-span-full bg-white rounded-3xl shadow-sm p-16 flex flex-col items-center justify-center text-center border border-slate-100">
              <div class="w-24 h-24 rounded-full flex items-center justify-center mb-6" style="background:linear-gradient(135deg, rgba(43,154,210,0.1), rgba(94,201,240,0.1));">
                <i class="ti ti-check text-5xl" style="color:#2B9AD2;"></i>
              </div>
              <h3 class="text-2xl font-black text-slate-900 mb-2">No Quote or Office-stage Jobs</h3>
              <p class="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">There are currently no quote-sent, ready-for-office, or closed jobs.</p>
            </div>
          } @else {
            @for (job of jobs(); track job.id) {
              <div class="job-card" [ngClass]="{ 'border-rose-300 ring-2 ring-rose-100 bg-rose-50/30': isSafetyDeclined(job) }">
                  <div class="card-header">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="job-status" [ngClass]="statusClass(job.status)">
                            {{job.status | uppercase}}
                        </span>
                        @if (isSafetyDeclined(job)) {
                          <span class="job-status status-error">
                            SAFETY DECLINED
                          </span>
                        }
                      </div>
                      <span class="text-xs font-bold text-slate-400">#{{job.id}}</span>
                  </div>

                  <div class="card-body">
                      <h3>{{job.customer}}</h3>
                      <p class="job-date"><i class="ti ti-map-pin"></i> {{job.address}}</p>
                      
                      <div class="bg-slate-50 p-3 rounded-lg border border-slate-100 mt-2">
                          <p class="text-xs text-slate-600 line-clamp-3">{{job.desc}}</p>
                      </div>

                      <div class="mt-3 text-xs text-slate-500 font-bold flex items-center gap-1">
                          <i class="ti ti-user text-[#2B9AD2]"></i> {{job.plumber}}
                      </div>
                      <div class="mt-2 rounded-lg border border-sky-100 bg-sky-50 px-2 py-1.5 text-[10px] font-semibold text-sky-800">
                        {{ officeChecklistLabel(job) }}
                      </div>
                      @if (officeMissingItems(job).length > 0) {
                        <div class="mt-2 flex flex-wrap gap-1.5">
                          @for (item of officeMissingItems(job); track item) {
                            <span class="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100 text-[10px] font-bold">{{ item }}</span>
                          }
                        </div>
                      }
                      @if (isSafetyDeclined(job)) {
                        <div class="mt-2 rounded-lg bg-rose-100 border border-rose-200 text-rose-800 px-2 py-1 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                          <i class="ti ti-shield-x"></i> Safety declined
                        </div>
                      }
                  </div>

                  <div class="card-footer">
                      <div class="flex gap-2">
                        <a [routerLink]="['/admin/dispatch/job', job.id]" class="btn-open">Open Detail</a>
                      </div>
                  </div>
              </div>
            }
          }
        </div>
        </div>
      } @else {
        @if (selectedJob(); as job) {
          <div class="max-w-4xl mx-auto space-y-5">
            <button (click)="selectJob(null)" class="flex items-center gap-1.5 text-slate-600 font-bold text-sm mb-2 hover:text-[#2B9AD2] transition-colors cursor-pointer"><i class="ti ti-arrow-left"></i> Back to Review List</button>
            <div class="bg-white rounded-3xl shadow-sm p-6 flex justify-between items-start border border-slate-100">
              <div><span class="text-[11px] font-bold text-slate-400">#{{job.id}}</span><h2 class="text-2xl font-black text-slate-900 mt-1">{{job.customer}}</h2><p class="text-sm text-slate-500 mt-1.5"><i class="ti ti-map-pin"></i> {{job.address}}</p></div>
              <div class="flex gap-3 flex-wrap justify-end">
                 <a [routerLink]="quoteRoute(job)" [queryParams]="quoteQueryParams(job)"
                    class="px-6 py-2.5 bg-[#2B9AD2] text-white rounded-xl font-bold shadow-md hover:bg-[#1B78AE] transition-all flex items-center gap-2 no-underline cursor-pointer">
                   <i class="ti ti-receipt text-[18px]"></i> Build/View Quote
                 </a>
                 <button (click)="markCompleted(job)" [disabled]="isCompleting()"
                    class="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold shadow-md hover:bg-emerald-700 transition-all flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                   @if (isCompleting()) {
                     <i class="ti ti-loader animate-spin text-[18px]"></i> Closing...
                   } @else {
                     <i class="ti ti-circle-check text-[18px]"></i> Mark Completed
                   }
                 </button>
                 <a [routerLink]="['/admin/dispatch/job', job.id]"
                    class="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2 no-underline">
                   <i class="ti ti-clipboard-data text-[18px]"></i> Review Evidence
                 </a>
              </div>
            </div>
          </div>
        }
      }
    </div>
  `
})
export class AdminDispatchReadyComponent implements OnInit {
  jobs = signal<any[]>([]);
  isLoading = signal(true);
  skeletons = [1, 2, 3, 4, 5, 6];
  selectedJob = signal<any | null>(null);
  isCompleting = signal(false);
  isCreatingInvoice = signal(false);

  private api = inject(ApiService);
  private router = inject(Router);

  ngOnInit() {
    this.loadJobs();
  }

  loadJobs() {
    this.isLoading.set(true);
    this.api.getJobs().subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success && res.data?.jobs) {
          const uniqueJobsMap = new Map();
          
          res.data.jobs
            .filter((j: any) => ['Quote Sent', 'Ready for Office', 'Completed', 'Terminated'].includes(j.status))
            .forEach((j: any) => {
               const jobId = j.job_id || j.id;
               if (!uniqueJobsMap.has(jobId)) {
                   uniqueJobsMap.set(jobId, {
                      id: jobId,
                      status: j.status,
                      customer: j.customer_name || 'Anonymous',
                      address: j.address || 'Unknown Address',
                      desc: j.description,
                      latestSafetyStatus: j.latest_safety_status || null,
                      photosCount: Number(j.photos_count || 0),
                      notesCount: Number(j.notes_count || 0),
                      materialsBlockingCount: Number(j.materials_blocking_count || 0),
                      latestMaterialBlocker: j.latest_material_blocker || '',
                      type: j.type || '',
                      quoteStatus: j.quote_status || '',
                      inspectionServiceDecision: j.inspection_service_decision || '',
                      linkedServiceCount: Number(j.linked_service_count || 0),
                      plumbers: new Set()
                   });
               }
               const existing = uniqueJobsMap.get(jobId);
               
               if (j.assigned_workers) {
                   j.assigned_workers.split(',').forEach((w: string) => existing.plumbers.add(w.trim()));
               } else if (j.worker_name || j.plumber_name) {
                   existing.plumbers.add(j.worker_name || j.plumber_name);
               }
            });

          this.jobs.set(Array.from(uniqueJobsMap.values()).map(job => {
            return {
              ...job,
              plumber: job.plumbers.size > 0 ? Array.from(job.plumbers).filter(Boolean).join(', ') : 'Unassigned'
            };
          }));
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  selectJob(job: any) { this.selectedJob.set(job); }

  assignedWorkerCount(): number {
    return this.jobs().filter(job => job.plumber && job.plumber !== 'Unassigned').length;
  }

  statusCount(status: string): number {
    return this.jobs().filter(job => job.status === status).length;
  }

  closedCount(): number {
    return this.jobs().filter(job => ['Completed', 'Terminated'].includes(job.status)).length;
  }

  safetyDeclinedCount(): number {
    return this.jobs().filter(job => this.isSafetyDeclined(job)).length;
  }

  isSafetyDeclined(job: any): boolean {
    return job?.latestSafetyStatus === 'Declined';
  }

  statusClass(status: string): string {
    return jobStatusClass(status);
  }

  officeChecklistLabel(job: any): string {
    if (job.status === 'Quote Sent') return 'Quote sent. Follow up customer approval before progressing the job.';
    if (this.isSafetyDeclined(job)) return 'Resolve safety concern before closing.';
    const missing = this.officeMissingItems(job);
    return missing.length ? `Needs review: ${missing.join(', ')}` : 'Close-out evidence looks ready. Open detail for final completion note.';
  }

  officeMissingItems(job: any): string[] {
    if (job.status === 'Quote Sent') return [];
    const missing: string[] = [];
    if (this.isSafetyDeclined(job)) missing.push('Safety unresolved');
    if (Number(job.photosCount || 0) === 0) missing.push('Missing photos');
    if (Number(job.notesCount || 0) === 0) missing.push('Missing notes');
    if (Number(job.materialsBlockingCount || 0) > 0) missing.push(job.latestMaterialBlocker || 'Parts pending');
    if (job.type === 'Inspection' && job.inspectionServiceDecision !== 'No Service Required' && Number(job.linkedServiceCount || 0) === 0 && ['Accepted', 'Approved'].includes(job.quoteStatus || '')) {
      missing.push('Service decision pending');
    }
    return missing;
  }

  openQuickActions(job: any) {
    if (!job?.id) return;
    this.api.getJob(job.id).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          this.selectedJob.set({
            id: res.data.id,
            status: res.data.status,
            customer: res.data.customer_name || 'Anonymous',
            address: res.data.address || 'Unknown Address',
            desc: res.data.description,
            plumber: (res.data.assigned_workers || []).map((w: any) => w.name).join(', ') || 'Unassigned',
            quote: res.data.quote || null
          });
        } else {
          showToast('error', res.message || 'Unable to load office actions for this job.');
        }
      },
      error: () => showToast('error', 'Unable to load office actions for this job.')
    });
  }

  quoteRoute(job: any): any[] {
    return job?.quote?.id ? ['/admin/financials/quotes', job.quote.id] : ['/admin/financials/quotes', 'new'];
  }

  quoteQueryParams(job: any): any {
    return job?.quote?.id ? null : { job_id: job?.id };
  }

  canCreateInvoice(job: any): boolean {
    const status = (job?.quote?.status || '').toString();
    return !!job?.quote?.id && (status === 'Accepted' || status === 'Approved');
  }

  createInvoice(job: any) {
    if (!job?.id) return;
    if (!job?.quote?.id) {
      showToast('error', 'Create or open a quote first, then generate the invoice.');
      return;
    }

    const quoteStatus = (job.quote.status || '').toString();
    if (quoteStatus !== 'Accepted' && quoteStatus !== 'Approved') {
      showToast('error', 'Invoice can be created only after quote acceptance.');
      return;
    }

    this.isCreatingInvoice.set(true);
    this.api.createInvoiceFromQuote(job.quote.id).subscribe({
      next: (res: any) => {
        this.isCreatingInvoice.set(false);
        if (res.success && res.data?.id) {
          showToast('success', 'Invoice created successfully.');
          this.router.navigate(['/admin/financials/invoices', res.data.id]);
        } else {
          showToast('error', res.message || 'Failed to create invoice.');
        }
      },
      error: () => {
        this.isCreatingInvoice.set(false);
        showToast('error', 'Failed to create invoice.');
      }
    });
  }

  markCompleted(job: any) {
    if (!job?.id || this.isCompleting()) return;
    const closeoutNote = typeof window !== 'undefined' ? window.prompt('Final office close-out note:') : '';
    if (!closeoutNote || closeoutNote.trim().length < 5) {
      showToast('error', 'Final office close-out note is required.');
      return;
    }
    let exceptionRemarks = '';
    const missing = this.officeMissingItems(job);
    if (missing.length > 0) {
      const entered = typeof window !== 'undefined' ? window.prompt(`Exception remarks required for: ${missing.join(', ')}`) : '';
      if (!entered || entered.trim().length < 5) {
        showToast('error', 'Exception remarks are required because checklist items are missing.');
        return;
      }
      exceptionRemarks = entered.trim();
    }

    this.isCompleting.set(true);
    this.api.updateJobStatus({ id: job.id, status: 'Completed', office_closeout_note: closeoutNote.trim(), office_exception_remarks: exceptionRemarks }).subscribe({
      next: (res: any) => {
        this.isCompleting.set(false);
        if (res.success) {
          showToast('success', 'Job marked as completed.');
          this.selectedJob.set(null);
          this.loadJobs();
        } else {
          showToast('error', res.message || 'Failed to complete job.');
        }
      },
      error: () => {
        this.isCompleting.set(false);
        showToast('error', 'Network error completing job.');
      }
    });
  }
}




